import { useState, type FormEvent } from "react";
import { CheckCircle, Clock, EnvelopeSimple } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";
import { Input } from "./Input";
import { useConfirm } from "./Confirm";
import { useApiErrorToast, useToast } from "./Toast";
import { useUpdateEmail, useUpdateNotifications } from "@/hooks/useAdminEvent";
import type { EventAdmin } from "@/lib/api";
import styles from "./EmailBlock.module.css";

type Props = {
  event: EventAdmin;
  shortcut: string;
  adminKey: string;
};

export function EmailBlock({ event, shortcut, adminKey }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiErrorToast();
  const confirm = useConfirm();
  const updateEmail = useUpdateEmail(shortcut, adminKey);
  const updateNotif = useUpdateNotifications(shortcut, adminKey);
  const [mode, setMode] = useState<"view" | "edit">(event.creatorEmail ? "view" : "edit");
  const [value, setValue] = useState(event.creatorEmail ?? "");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmed = value.trim();
    if (!trimmed) {
      setError(t("admin.emailLabel"));
      return;
    }
    try {
      await updateEmail.mutateAsync(trimmed);
      toast.success(t("admin.emailSentToast"));
      setMode("view");
    } catch (err) {
      apiError(err, t("common.errorGeneric"));
    }
  };

  const resend = async () => {
    if (!event.creatorEmail) return;
    try {
      await updateEmail.mutateAsync(event.creatorEmail);
      toast.success(t("admin.emailSentToast"));
    } catch (err) {
      apiError(err, t("common.errorGeneric"));
    }
  };

  const remove = async () => {
    const r = await confirm({
      title: t("admin.emailRemoveConfirm"),
      variant: "danger",
      confirmLabel: t("admin.emailRemove"),
    });
    if (!r.confirmed) return;
    try {
      await updateEmail.mutateAsync(null);
      toast.success(t("admin.emailRemovedToast"));
      setValue("");
      setMode("edit");
    } catch (err) {
      apiError(err, t("common.errorGeneric"));
    }
  };

  const toggleNotif = async (patch: { notifyOnChanges?: boolean; notifySummary?: boolean }) => {
    try {
      await updateNotif.mutateAsync({
        notifyOnChanges: patch.notifyOnChanges ?? event.notifyOnChanges,
        notifySummary: patch.notifySummary ?? event.notifySummary,
      });
      toast.success(t("admin.notifyUpdatedToast"));
    } catch (err) {
      apiError(err, t("common.errorGeneric"));
    }
  };

  const canToggleNotif = Boolean(event.creatorEmail) && event.creatorEmailVerified;

  return (
    <section className={styles.block}>
      <h3 className={styles.heading}>
        <EnvelopeSimple size={18} weight="regular" />
        {t("admin.emailHeading")}
      </h3>

      {mode === "edit" ? (
        <form className={styles.editForm} onSubmit={submit}>
          <Input
            label={t("admin.emailLabel")}
            type="email"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="email"
            maxLength={254}
            hint={event.creatorEmail ? undefined : t("admin.emailHintNone")}
            error={error}
          />
          <div className={styles.editActions}>
            {event.creatorEmail && (
              <Button
                type="button"
                variant="secondary"
                size="compact"
                onClick={() => {
                  setValue(event.creatorEmail ?? "");
                  setError(null);
                  setMode("view");
                }}
              >
                {t("common.cancel")}
              </Button>
            )}
            <Button type="submit" size="compact" disabled={updateEmail.isPending || !value.trim()}>
              {t("admin.emailSave")}
            </Button>
          </div>
        </form>
      ) : (
        <div className={styles.viewBlock}>
          <div className={styles.addressRow}>
            <span className={styles.address}>{event.creatorEmail}</span>
            <span
              className={
                event.creatorEmailVerified
                  ? styles.statusVerified
                  : styles.statusPending
              }
            >
              {event.creatorEmailVerified ? (
                <>
                  <CheckCircle size={14} weight="regular" /> {t("admin.emailVerified")}
                </>
              ) : (
                <>
                  <Clock size={14} weight="regular" /> {t("admin.emailNotVerified")}
                </>
              )}
            </span>
          </div>
          {!event.creatorEmailVerified && (
            <p className={styles.pendingHint}>{t("admin.emailPending")}</p>
          )}
          <div className={styles.viewActions}>
            {!event.creatorEmailVerified && (
              <Button
                type="button"
                variant="secondary"
                size="compact"
                onClick={resend}
                disabled={updateEmail.isPending}
              >
                {t("admin.emailResend")}
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="compact"
              onClick={() => {
                setValue(event.creatorEmail ?? "");
                setMode("edit");
              }}
            >
              {t("admin.emailChange")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="compact"
              onClick={remove}
              disabled={updateEmail.isPending}
            >
              {t("admin.emailRemove")}
            </Button>
          </div>
        </div>
      )}

      <fieldset className={styles.notifyFieldset} disabled={!canToggleNotif}>
        <label className={styles.notifyRow}>
          <input
            type="checkbox"
            checked={event.notifyOnChanges}
            onChange={(e) => toggleNotif({ notifyOnChanges: e.target.checked })}
            disabled={!canToggleNotif || updateNotif.isPending}
          />
          <span>
            <strong>{t("admin.notifyOnChangesLabel")}</strong>
            <span className={styles.notifyHint}>{t("admin.notifyOnChangesHint")}</span>
          </span>
        </label>
        <label className={styles.notifyRow}>
          <input
            type="checkbox"
            checked={event.notifySummary}
            onChange={(e) => toggleNotif({ notifySummary: e.target.checked })}
            disabled={!canToggleNotif || updateNotif.isPending}
          />
          <span>
            <strong>{t("admin.notifySummaryLabel")}</strong>
            <span className={styles.notifyHint}>{t("admin.notifySummaryHint")}</span>
          </span>
        </label>
        {!canToggleNotif && (
          <p className={styles.lockedHint}>{t("admin.notifyLockedHint")}</p>
        )}
      </fieldset>
    </section>
  );
}
